/**
 * Which period a metric reports, and what it compares against.
 *
 * A metric tile shows ONE grain — never a summed range. Utilization is a
 * ratio, and adding twelve months of it produces a number that is not a
 * quantity of anything; a rule that summed buckets would be right for hours
 * and quietly wrong for every percentage on the model.
 *
 * Two rules decide which grain, and the second exists because of what the
 * first does on the first of the month:
 *
 *  1. **The last COMPLETE grain.** On 1 September a monthly series ending in
 *     September ends with a bucket one day old. Reporting it against a full
 *     August reads as a 99% collapse, every month, on the same day — the most
 *     confidently wrong number this surface could produce.
 *
 *  2. **Unless the reader asked for the current one.** Someone who selects
 *     "Current month" is asking for month-to-date and should get it. It is
 *     shown, said to be partial, and given NO comparison: a part-month against
 *     a whole one is not a movement, and an arrow drawn on it is a lie with a
 *     direction. The history stays, because the shape is still true.
 *
 * THE GRAIN IS TOLD, NOT GUESSED. It used to be read off the shape of the key,
 * which works until a model writes a period in a format that looks like a
 * different one — and one does: Budget Period is a MONTH keyed as
 * `2026-08-01`, because it sorts chronologically as text. Read as a day, two
 * consecutive budget months were 31 days apart and therefore never adjacent,
 * so a budgets metric silently offered no comparison at all; and mid-month,
 * a period running to the 30th was called finished on the 15th. The dimension
 * knows what it groups by, so it is asked.
 *
 * Pure and injectable: `today` is a parameter, so the rules can be tested on
 * the day that breaks them rather than only on days that happen not to.
 */

import { addIsoDays } from '@clockwise/shared/iso-date';

/** The size of one bucket. */
export type Grain = 'day' | 'week' | 'month';

const MONTH = /^(\d{4})-(\d{2})$/;
const WEEK = /^(\d{4})-W(\d{2})$/;
const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
/** A period that spells out its own bounds: `start/end`, end EXCLUSIVE —
 *  Budget Period's quarters, halves and years (`2026-07-01/2026-10-01`). */
const INTERVAL = /^(\d{4}-\d{2}-\d{2})\/(\d{4}-\d{2}-\d{2})$/;

/**
 * What a time dimension groups by.
 *
 * By ID, because a `Dimension` only says `isTime: true`. The models name their
 * period dimensions `date`, `week` and `month`; anything else that is a time
 * dimension is a named period — Budget Period, and whatever follows it — and
 * those are months.
 *
 * ONE definition, used both to widen a metric's window and to read its series,
 * so the two halves of the feature cannot disagree about what a period is.
 */
export function grainOfDimension(dimensionId: string): Grain {
  if (dimensionId === 'week') return 'week';
  if (dimensionId === 'date') return 'day';
  return 'month';
}

/** `YYYY-MM-DD` for a Date, in UTC. */
const iso = (d: Date): string => d.toISOString().slice(0, 10);

/**
 * The first day a key covers, whatever notation it is written in.
 *
 * The three the models emit: `2026-09` (month), `2026-W27` (ISO week), and a
 * plain `2026-06-29` — which is a day for the Timesheet Date dimension and the
 * first of the month for Budget Period. Which of those it means is the GRAIN's
 * business, not this function's; all this does is find the date the key names.
 */
function keyStart(key: string): string | null {
  const interval = INTERVAL.exec(key);
  if (interval) return interval[1] as string;
  if (MONTH.test(key)) return `${key}-01`;
  if (DAY.test(key)) return key;
  const week = WEEK.exec(key);
  if (week) {
    const year = Number(week[1]);
    const w = Number(week[2]);
    // ISO weeks: week 1 contains 4 January, and weeks start on Monday.
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const isoDow = (jan4.getUTCDay() + 6) % 7;
    const week1Monday = new Date(jan4);
    week1Monday.setUTCDate(jan4.getUTCDate() - isoDow);
    const monday = new Date(week1Monday);
    monday.setUTCDate(week1Monday.getUTCDate() + (w - 1) * 7);
    return iso(monday);
  }
  return null;
}

/** The first day the period covers, inclusive. `null` for an unreadable key. */
export function grainStart(key: string): string | null {
  return keyStart(key);
}

/**
 * The last day the period covers, inclusive, as `YYYY-MM-DD`.
 *
 * `null` when the key names no date — a caller then has no basis to call the
 * bucket complete or partial, and treats it as complete rather than inventing
 * a boundary.
 */
export function grainEnd(key: string, grain: Grain): string | null {
  // A key that names its own end is that long, whatever the grain says.
  const interval = INTERVAL.exec(key);
  if (interval) return addIsoDays(interval[2] as string, -1);
  const start = keyStart(key);
  if (start === null) return null;
  const d = new Date(`${start}T00:00:00Z`);
  if (grain === 'day') return start;
  if (grain === 'week') {
    d.setUTCDate(d.getUTCDate() + 6);
    return iso(d);
  }
  // Day 0 of the NEXT month is the last day of this one, which is how this
  // avoids a table of month lengths and a leap-year branch.
  return iso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)));
}

/** Has this period finished? One ending today is not yet complete. */
export function isComplete(key: string, grain: Grain, today: string): boolean {
  const end = grainEnd(key, grain);
  // An unreadable key has no boundary to be past, so it is taken at face value
  // rather than being called partial on a guess.
  return end === null ? true : end < today;
}

export interface MetricSelection {
  /** Index into the series of the period the metric reports. */
  current: number;
  /** What the movement is measured against. Absent means no comparison. */
  previous?: number;
  /** The reported period has not finished. Say so; never compare it. */
  toDate: boolean;
}

export interface SelectMetricPeriodsInput {
  /** The series' period keys, oldest first. */
  keys: readonly string[];
  /**
   * What those keys group by, from the DIMENSION rather than from their shape.
   *
   * Budget Period is a month written `2026-08-01`; read as a day, two
   * consecutive budget months are 31 days apart and never adjacent, so the
   * comparison silently vanishes. See {@link grainOfDimension}.
   */
  grain: Grain;
  /**
   * The window the READER chose, which is not necessarily the window queried.
   *
   * A metric widens its query backwards so the sparkline has history, so the
   * series routinely reaches further back than the reader asked. This is what
   * distinguishes "show me September" from "show me the year, which happens to
   * contain September".
   */
  selectedFrom: string;
  selectedTo: string;
  /** `YYYY-MM-DD`. Injectable so the rule is testable on the day it matters. */
  today: string;
}

/**
 * Pick the reported period and its comparison.
 *
 * Returns `null` when there is nothing to report — an empty series has no
 * period to name, and a caller should render its own empty state rather than
 * be handed an index into nothing.
 */
export function selectMetricPeriods(input: SelectMetricPeriodsInput): MetricSelection | null {
  const { keys, grain, selectedFrom, selectedTo, today } = input;
  if (keys.length === 0) return null;

  // THE READER ASKED FOR THIS ONE GRAIN. Detected from the SELECTED window
  // rather than from the series: a window lying inside a single bucket is
  // somebody asking about that bucket, whereas the same bucket at the end of a
  // year-long window is just where the year got to.
  const selected = keys.filter((key) => overlaps(key, grain, selectedFrom, selectedTo));
  if (selected.length === 1) {
    const only = selected[0] as string;
    if (!isComplete(only, grain, today)) {
      // Month-to-date: shown, flagged, and deliberately uncompared.
      return { current: keys.indexOf(only), toDate: true, previous: undefined };
    }
  }

  // Otherwise the last grain that has actually finished. Everything after it
  // is either partial or has not started.
  let current = -1;
  for (let i = keys.length - 1; i >= 0; i--) {
    if (isComplete(keys[i] as string, grain, today)) {
      current = i;
      break;
    }
  }
  // Every grain is still running — a series of one partial bucket, which is
  // the same situation as above and gets the same treatment.
  if (current === -1) return { current: keys.length - 1, toDate: true, previous: undefined };

  // ONLY IF IT ACTUALLY PRECEDES. The series comes from a GROUP BY, which
  // emits no row for a period with no data, so the entry before this one in
  // the array is not necessarily the period before it in time. A customer
  // between engagements or somebody back from three months' leave would
  // otherwise have August quietly compared against May and labelled "vs prior
  // period" — wrong in a way nothing on the tile discloses.
  //
  // No comparison is the honest answer: the reader sees the value and its
  // history, and is not told a movement that did not happen.
  const adjacent =
    current > 0 && isAdjacent(keys[current - 1] as string, keys[current] as string, grain);
  return { current, previous: adjacent ? current - 1 : undefined, toDate: false };
}

/**
 * Does `earlier` end exactly where `later` begins — one grain, no gap?
 *
 * Compared by DATE rather than by counting keys, so it works across a year
 * boundary and across all three grains without a case per grain.
 */
function isAdjacent(earlier: string, later: string, grain: Grain): boolean {
  // And the SAME LENGTH: June ends where Q3 begins, but a quarter is not
  // compared with a month (CLO-134). Q2 → Q3 is.
  if (spanOf(earlier) !== spanOf(later)) return false;
  const end = grainEnd(earlier, grain);
  const start = grainStart(later);
  if (end === null || start === null) return false;
  const next = new Date(`${end}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return iso(next) === start;
}

/** How many months a `start/end` key spans; `null` for a one-grain key. */
function spanOf(key: string): number | null {
  const m = INTERVAL.exec(key);
  if (!m) return null;
  const [sy, sm] = (m[1] as string).split('-').map(Number) as [number, number];
  const [ey, em] = (m[2] as string).split('-').map(Number) as [number, number];
  return (ey - sy) * 12 + (em - sm);
}

/** Does this period overlap the window at all? */
function overlaps(key: string, grain: Grain, from: string, to: string): boolean {
  const end = grainEnd(key, grain);
  if (end === null) return false;
  const start = grainStart(key);
  if (start === null) return false;
  return start <= to && end >= from;
}

/** A concrete, inclusive `YYYY-MM-DD` window. */
export interface DateWindow {
  from: string;
  to: string;
}

export interface ComparisonWindows {
  /**
   * The window the figure reports: the one the reader chose, cut at today
   * while it is still running.
   */
  current: DateWindow;
  /**
   * Its MIRROR: the same span immediately before it — the previous quarter
   * for a quarter, the previous year for a year — cut to the SAME POINT when
   * `current` is. Quarter-to-date is compared with the previous quarter to the
   * same day, never with the whole of it: a part-period against a whole one
   * is not a movement. The exception is a single MONTH, which is compared
   * with the whole of last month even while it runs — the figure a reader
   * already knows and is tracking this month against.
   */
  previous: DateWindow;
  /** The chosen window has started and not finished. */
  toDate: boolean;
  /** What `current` is called — `Q3 2026`, `Aug 2026`, `2026`. */
  currentLabel: string;
  /**
   * What `previous` is called, beside `currentLabel` — `Q2`, or `Dec 2025`
   * when the year differs. NAMED, NOT CUT: quarter-to-date is compared with
   * Q2 to the same day, but "vs Q2 to Jun 29" on every tile spelled out a rule
   * that holds for all of them. Only a month, a quarter or a year carries
   * that rule in its name; any other mirror that was cut (a custom range) is
   * labelled by the dates compared — `Jun 1 – Jul 10` — not `Jun – Jul`.
   */
  previousLabel: string;
  /** The dates `previous` actually covers — `Apr 1 – Jun 29, 2026` — for a hover. */
  previousRange: string;
}

/**
 * The window a metric reports and the mirror it is compared against.
 *
 * THE READER'S WINDOW DECIDES BOTH. A dashboard set to "This quarter" is asking
 * about the quarter, so the figure is the quarter and the comparison is the
 * quarter before it — not the last month inside it against the month before
 * that, which is what reporting one grain of the series used to answer.
 *
 * A window of WHOLE MONTHS mirrors by months, so a quarter's mirror is a
 * quarter and a year's is a year whatever the month lengths; anything else
 * (a custom range) mirrors by its length in days.
 */
export function comparisonWindows(input: {
  from: string;
  to: string;
  /** `YYYY-MM-DD`. */
  today: string;
}): ComparisonWindows {
  const { from, to, today } = input;
  const toDate = from <= today && today <= to;
  const end = toDate ? today : to;

  const months = wholeMonths(from, to);
  let mirror: DateWindow;
  let mirrorEnd: string;
  if (months !== null) {
    mirror = { from: shiftMonths(from, -months), to: monthEndOf(shiftMonths(to, -months)) };
    // A MONTH is compared with the whole of the month before, even while it
    // runs: that is the number people already know ("last month's hours") and
    // what they measure this month against (decided 2026-09-29). Longer
    // windows are cut to the same day of the month, `months` back — clamped,
    // so the 31st of May mirrors to the 30th of April rather than spilling
    // into May.
    mirrorEnd = toDate && months > 1 ? shiftMonths(end, -months) : mirror.to;
  } else {
    const days = daysBetween(from, to) + 1;
    mirror = { from: addIsoDays(from, -days), to: addIsoDays(from, -1) };
    mirrorEnd = toDate ? addIsoDays(mirror.from, daysBetween(from, end)) : mirror.to;
  }

  // Named for the whole mirror only when that name is a calendar unit, whose
  // same-day cut is the rule every preset follows. Otherwise the dates compared.
  const named = describeWindow(
    mirror.from,
    isCalendarUnit(mirror.from, mirror.to) ? mirror.to : mirrorEnd,
  );
  // The year once, not twice: beside "Q3 2026" the mirror reads "Q2", and
  // only a mirror in another year says which. A whole-year label IS its year.
  const year = from.slice(0, 4);
  const short =
    mirror.from.slice(0, 4) === year && named !== year
      ? named.replace(`, ${year}`, '').replace(` ${year}`, '')
      : named;
  return {
    current: { from, to: end },
    previous: { from: mirror.from, to: mirrorEnd },
    toDate,
    currentLabel: describeWindow(from, to),
    previousLabel: short,
    previousRange: describeRange(mirror.from, mirrorEnd),
  };
}

/** Month abbreviations, January first — the one table every period label reads. */
export const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * A window as a reader names it: `Aug 2026`, `Q3 2026`, `2026`, `Jul – Sep
 * 2026`, or two dates for a range that does not fall on month boundaries.
 */
export function describeWindow(from: string, to: string): string {
  const [fy, fm] = ymd(from);
  const [ty, tm] = ymd(to);
  const months = wholeMonths(from, to);
  if (months !== null) {
    const start = MONTH_NAMES[fm - 1];
    const stop = MONTH_NAMES[tm - 1];
    if (months === 1) return `${start} ${fy}`;
    if (months === 3 && (fm - 1) % 3 === 0) return `Q${(fm - 1) / 3 + 1} ${fy}`;
    if (months === 12 && fm === 1) return `${fy}`;
    return fy === ty ? `${start} – ${stop} ${fy}` : `${start} ${fy} – ${stop} ${ty}`;
  }
  return describeRange(from, to);
}

/** Is this window one month, one calendar quarter or one calendar year? */
function isCalendarUnit(from: string, to: string): boolean {
  const months = wholeMonths(from, to);
  const [, fm] = ymd(from);
  return months === 1 || (months === 3 && (fm - 1) % 3 === 0) || (months === 12 && fm === 1);
}

/** Always dates, never a name: `Apr 1 – Jun 29, 2026`. */
function describeRange(from: string, to: string): string {
  const [fy] = ymd(from);
  const [ty] = ymd(to);
  if (from === to) return `${dayLabel(from)}, ${fy}`;
  return fy === ty
    ? `${dayLabel(from)} – ${dayLabel(to)}, ${fy}`
    : `${dayLabel(from)}, ${fy} – ${dayLabel(to)}, ${ty}`;
}

/** `Jun 29`. */
function dayLabel(date: string): string {
  const [, m, d] = ymd(date);
  return `${MONTH_NAMES[m - 1]} ${d}`;
}

function ymd(date: string): [number, number, number] {
  return [Number(date.slice(0, 4)), Number(date.slice(5, 7)), Number(date.slice(8, 10))];
}

/** Last day of the month `date` falls in. */
function monthEndOf(date: string): string {
  return grainEnd(date.slice(0, 7), 'month') as string;
}

/**
 * How many whole calendar months the window covers, or `null` when it does not
 * start on a 1st and end on a month's last day.
 */
function wholeMonths(from: string, to: string): number | null {
  if (!from.endsWith('-01') || monthEndOf(to) !== to || to < from) return null;
  const [fy, fm] = ymd(from);
  const [ty, tm] = ymd(to);
  return (ty - fy) * 12 + (tm - fm) + 1;
}

/** `date` moved by `n` months, its day clamped to the target month's length. */
function shiftMonths(date: string, n: number): string {
  const [y, m, d] = ymd(date);
  const lastDay = new Date(Date.UTC(y, m - 1 + n + 1, 0)).getUTCDate();
  return iso(new Date(Date.UTC(y, m - 1 + n, Math.min(d, lastDay))));
}

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}
